import * as signalR from "@microsoft/signalr";
import * as React from "react";

import { AuthenticationService } from "@app/authentication";
import Message from "./message";

type SubscribedFunc = (message: Message) => void;

/** Provides a means to subscribe and unsubscribe from notifications. */
interface NotificationContextProps {
  children?: React.ReactNode;
  subscribe: (subscribedFunc: SubscribedFunc) => void;
  unsubscribe: (subscribedFunc: SubscribedFunc) => void;
}

/** Provides means of receiving notifications. */
export const NotificationContext = React.createContext<NotificationContextProps>(null);

/**
 * Wraps a component and sets up a mechanism for storing subscribed callbacks.
 * @param Component The component to wrap.
 */
function provideNotificationContext<T = unknown>(Component: React.ComponentType<React.PropsWithChildren<T>>): React.ComponentType<React.PropsWithChildren<T>> {
  class ProvideNotificationContext extends React.PureComponent<T, NotificationContextProps> {
    private subscriptions: Set<SubscribedFunc> = new Set<SubscribedFunc>();
    private connection: signalR.HubConnection;

    constructor(props: T) {
      super(props);

      this.state = {
        subscribe: this.handleSubscribe,
        unsubscribe: this.handleUnsubscribe,
      };
    }

    public async componentDidMount() {
      try {
        const conn = await this.getConnection();
        if (conn) {
          await conn.start();
          this.connection.on("messageReceived", (messageJson: string) => {
            const message = JSON.parse(messageJson);
            this.subscriptions.forEach((subscription) => subscription(message));
          });
        }
      } catch (e) {
        console.error(e);
      }
    }

    public async componentWillUnmount() {
      try {
        const conn = await this.getConnection();
        if (conn) {
          await conn.stop();
        }
      } catch (e) {
        console.error(e);
      }
    }

    public render() {
      return (
        <NotificationContext.Provider value={this.state}>
          <Component {...this.props} />
        </NotificationContext.Provider>
      );
    }

    /// builds a connection to the signalR hub - ONLY if the user is authenticated
    private getConnection = async () => {
      if (!this.connection) {
        await AuthenticationService.getInstance().loadUser();
        const user = await AuthenticationService.getInstance().getUser();
        if (user) {
          this.connection = new signalR.HubConnectionBuilder()
            .withUrl(`${window.apiEndpoint}/hub`, {
              accessTokenFactory: () => {
                return user.access_token;
              },
            })
            .build();
        }
      }

      return this.connection;
    }

    private handleSubscribe = (subscribedFunc: SubscribedFunc) => {
      this.subscriptions.add(subscribedFunc);
    }

    private handleUnsubscribe = (subscribedFunc: SubscribedFunc) => {
      this.subscriptions.delete(subscribedFunc);
    }
  }

  return ProvideNotificationContext;
}

function withNotificationContext<T>(Component: React.ComponentType<React.PropsWithChildren<T & NotificationContextProps>>): React.ComponentType<React.PropsWithChildren<T>> {
  class WithNotificationContext extends React.PureComponent<T> {
    public render() {
      return (
        <NotificationContext.Consumer>
          {(contextProps) => <Component {...this.props} {...contextProps} />}
        </NotificationContext.Consumer>
      );
    }
  }

  return WithNotificationContext;
}

export {
  NotificationContextProps,
  provideNotificationContext,
  SubscribedFunc,
  withNotificationContext,
};
